import {
  type AccountInfo,
  type EndSessionRequest,
  InteractionRequiredAuthError,
  InteractionStatus,
  type SilentRequest,
} from "@azure/msal-browser";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider, useMsal } from "@azure/msal-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { setTokenProvider, setUnauthorizedHandler } from "../api/client";
import { apiRequest, isAuthConfigured, loginRequest, msalConfig } from "./authConfig";

export interface AuthContextValue {
  /** Current user account (null when not authenticated). */
  user: AccountInfo | null;
  /** MSAL interaction status (e.g. None, Login, Logout, HandleRedirect). */
  interactionStatus: InteractionStatus;
  /** Start sign-in (redirects to Microsoft Entra ID). */
  login: () => Promise<void>;
  /** Sign out (redirects to Entra sign-out). */
  logout: () => Promise<void>;
  /** Get access token for API calls. Resolves to null if no API scope or not authenticated. */
  getAccessToken: () => Promise<string | null>;
  /** Last authentication error message, if any. */
  error: string | null;
  /** Clear the current error. */
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function createMsalInstance() {
  return new PublicClientApplication(msalConfig);
}

const msalInstance = createMsalInstance();

// MSAL Browser 3.0+ requires initialization before use
const msalInitPromise = msalInstance.initialize();

/**
 * Parse the URL hash fragment for OAuth 2.0 error responses from Microsoft Entra ID.
 * Entra redirects with: /#error=<code>&error_description=<encoded_description>&state=<state>
 * Returns the parsed error or null if no error is present.
 */
function parseHashError(): { error: string; errorDescription: string } | null {
  const hash = window.location.hash;
  if (!hash || !hash.includes("error=")) return null;

  const params = new URLSearchParams(hash.substring(1));
  const error = params.get("error");
  const errorDescription = params.get("error_description");

  if (!error) return null;

  return {
    error,
    errorDescription: errorDescription
      ? decodeURIComponent(errorDescription.replace(/\+/g, " "))
      : error,
  };
}

/**
 * Capture any Entra ID error from the URL hash at module-evaluation time, **before**
 * MSAL's handleRedirectPromise() processes (and clears) the hash fragment.
 * This ensures errors like redirect_uri_mismatch are always displayed.
 */
const initialHashError = parseHashError();

function AuthContextProviderInner({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const [error, setError] = useState<string | null>(
    initialHashError?.errorDescription ?? null
  );

  const user = accounts[0] ?? null;

  // Clean the hash from the URL if there was an auth error, without triggering a navigation
  useEffect(() => {
    if (initialHashError && window.history.replaceState) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, []);

  const login = useCallback(async () => {
    setError(null);
    
    if (!isAuthConfigured) {
      const message = "Authentication is not configured. Please set VITE_ENTRA_CLIENT_ID and VITE_ENTRA_TENANT_ID environment variables.";
      setError(message);
      return;
    }
    
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sign in failed";
      setError(message);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    setError(null);
    try {
      const logoutRequest: EndSessionRequest = {
        account: user ?? undefined,
      };
      await instance.logoutRedirect(logoutRequest);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Sign out failed";
      setError(message);
    }
  }, [instance, user]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!apiRequest || !user) return null;
    setError(null);
    const request: SilentRequest = {
      ...apiRequest,
      account: user,
    };
    try {
      const response = await instance.acquireTokenSilent(request);
      // Fall back to the ID token (signed JWT with user claims) when no access token is returned.
      const token = response.accessToken || response.idToken;
      return token || null;
    } catch (silentErr) {
      if (silentErr instanceof InteractionRequiredAuthError) {
        try {
          const response = await instance.acquireTokenPopup(request);
          // Fall back to ID token if no access token
          return response.accessToken || response.idToken || null;
        } catch (popupErr) {
          const message = popupErr instanceof Error ? popupErr.message : "Failed to get token";
          setError(message);
          return null;
        }
      }
      const message = silentErr instanceof Error ? silentErr.message : "Failed to get token";
      setError(message);
      return null;
    }
  }, [instance, user]);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    setTokenProvider(getAccessToken);
    // Register handler for 401 responses - trigger re-authentication (token expired or invalid)
    setUnauthorizedHandler(() => {
      if (!isAuthConfigured) return;
      // Always trigger login on 401: user not signed in or token expired (MSAL will refresh or redirect)
      login().catch(() => {
        // Login redirect will handle the error
      });
    });
    return () => {
      setTokenProvider(null);
      setUnauthorizedHandler(null);
    };
  }, [getAccessToken, login]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      interactionStatus: inProgress,
      login,
      logout,
      getAccessToken,
      error,
      clearError,
    }),
    [user, inProgress, login, logout, getAccessToken, error, clearError]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth provider that wraps the app with MSAL and exposes auth state and actions
 * via AuthContext. Use useAuth() to consume.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    msalInitPromise
      .then(() => {
        setIsInitialized(true);
      })
      .catch((err) => {
        setInitError(err instanceof Error ? err.message : "MSAL initialization failed");
        setIsInitialized(true); // Still set initialized so the UI can show the error
      });
  }, []);

  // Show loading while MSAL initializes
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="text-text-tertiary">Initializing...</span>
      </div>
    );
  }

  // Show error if initialization failed
  if (initError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="text-error">Authentication initialization failed: {initError}</span>
      </div>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthContextProviderInner>{children}</AuthContextProviderInner>
    </MsalProvider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx == null) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }
  return ctx;
}

/** Re-export so consumers can derive loading / sign-in vs sign-out without depending on MSAL. */
export { InteractionStatus } from "@azure/msal-browser";
